import type { ReactElement, SyntheticEvent } from "react"
import { useContext } from "react"
import { Link } from "gatsby"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import FormilyFormButton from "@gatsby-plugin-generic-page/components/FormilyFormButton"
import PriceWithButtonWrapper from "@gatsby-plugin-generic-page/components/PriceWithButtonWrapper"
import ProductPrice from "@gatsby-plugin-generic-page/components/ProductPrice"
import UdsBasketButton from "@gatsby-plugin-generic-page/components/UdsBasketButton"
import UdsFastCheckoutButton from "@gatsby-plugin-generic-page/components/UdsFastCheckoutButton"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import type { Formily } from "@gatsby-plugin-generic-page/fragments/formily"
import buildButtonChildren from "@gatsby-plugin-generic-page/helpers/buildButtonChildren"
import { getUdsButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { replaceLinkValues } from "@gatsby-plugin-generic-page/helpers/linkParser"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { loadIcon as loadSystemIcon } from "@ionos-web-design-system/icon/system/loader"
import {
  Button,
  ButtonIcon,
  Link as LinkUds,
  Skeleton,
  standardIconSizeMap,
  useDynamicIcon,
} from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import SearchUrlParametersContext from "~/context/SearchUrlParametersContext"
import type { GaEvent } from "~/fragments/gaEvent"
import useAnchorProperties from "~/hooks/useAnchorProperties"
import useModalState from "~/hooks/useModalState"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { showPlanOverLimitModal } from "~/store/basket/actions"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"
import CtaModal from "../CtaModal"

type Props = {
  button: CtaElement
  isExpandedOnMobile?: boolean
}

function UdsButton({
  button,
  isExpandedOnMobile,
}: Props): ReactElement<Props> | null {
  const dispatch = useAppDispatch()
  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)
  const { currentPriceType } = usePriceTypeContext()
  const {
    actionTypeName,
    text,
    title,
    concept,
    variant,
    size,
    isExpanded,
    isExpandedOnMobile: isExpandedOnMobileFromData,
    showPrice,
    gaEvent,
    anchorId,
    form,
    href,
    openInNewTab,
    pages,
    modalTitle,
    texts,
    plan,
    fastCheckoutPlan,
    buttonType,
    icon,
  } = getUdsButtonFlatData(button, currentPriceType)
  const { isOpen, open, close } = useModalState(gaEvent as GaEvent)
  const currentSearchUrlParameters = useContext(SearchUrlParametersContext)
  const { href: anchorHref, onClick: anchorOnClick } =
    useAnchorProperties(anchorId)

  const { icon: loadedIcon, isLoading: isIconLoading } = useDynamicIcon(
    loadSystemIcon,
    icon?.iconId ?? null
  )

  const resolvedIsExpandedOnMobile =
    isExpandedOnMobile || isExpandedOnMobileFromData

  if (isEmpty(actionTypeName)) {
    return null
  }

  let ctaButton: ReactElement | null = null

  const expandedClassName = isExpanded
    ? "w-full"
    : resolvedIsExpandedOnMobile
      ? "w-full md:w-auto md:w-fit"
      : undefined

  const commonButtonProps = {
    concept,
    variant,
    size,
    title,
    className: expandedClassName,
  }

  const commonButtonIconProps = {
    concept,
    variant,
    size,
    iconTitle: title,
    className: expandedClassName,
  }

  const isSolo = buttonType === "icon-solo" ? loadedIcon : null
  const isSoloLoading = buttonType === "icon-solo" ? isIconLoading : false

  let iconNode: ReactElement | null = null

  if (!isEmpty(icon)) {
    if (isIconLoading) {
      iconNode = (
        <Skeleton
          variant="rectangle"
          width={standardIconSizeMap[size]}
          height={standardIconSizeMap[size]}
        />
      )
    } else if (loadedIcon) {
      iconNode = <Button.Icon icon={loadedIcon} size={size} />
    }
  }

  const buttonChildren = buildButtonChildren(buttonType, iconNode, text)

  const renderButton = (
    onClick?: (event: SyntheticEvent) => void
  ): ReactElement => {
    if (isSoloLoading) {
      return (
        <Skeleton
          variant="rectangle"
          width={standardIconSizeMap[size]}
          height={standardIconSizeMap[size]}
        />
      )
    }

    if (isSolo) {
      return (
        <ButtonIcon
          icon={isSolo}
          {...commonButtonIconProps}
          onClick={onClick}
        />
      )
    }

    return (
      <Button {...commonButtonProps} onClick={onClick}>
        {buttonChildren}
      </Button>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.SCROLL_TO_SECTION) &&
    !isEmpty(anchorId)
  ) {
    const handleAnchorClick = (event: SyntheticEvent) => {
      sendGaEvent(gaEvent as GaEvent)

      if (anchorOnClick) {
        anchorOnClick(event)
      }
    }

    ctaButton = isSoloLoading ? (
      <Skeleton
        variant="rectangle"
        width={standardIconSizeMap[size]}
        height={standardIconSizeMap[size]}
      />
    ) : isSolo ? (
      <ButtonIcon asChild {...commonButtonIconProps} icon={isSolo}>
        <Link title={title} to={anchorHref} onClick={handleAnchorClick} />
      </ButtonIcon>
    ) : (
      <Button asChild {...commonButtonProps}>
        <Link
          title={commonButtonProps.title}
          to={anchorHref}
          onClick={handleAnchorClick}
        >
          {buttonChildren}
        </Link>
      </Button>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_FORM) &&
    !isEmpty(form)
  ) {
    ctaButton = (
      <FormilyFormButton
        button={(onClick) => renderButton(onClick)}
        formilyForm={form[0] as Formily}
      />
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.OPEN_MODAL) &&
    !isEmpty(texts)
  ) {
    ctaButton = (
      <>
        {renderButton(open)}

        <CtaModal
          isOpen={isOpen}
          texts={texts}
          title={modalTitle}
          onCloseButtonClick={close}
        />
      </>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.INTERNAL_LINK) &&
    !isEmpty(pages)
  ) {
    ctaButton = isSoloLoading ? (
      <Skeleton
        variant="rectangle"
        width={standardIconSizeMap[size]}
        height={standardIconSizeMap[size]}
      />
    ) : isSolo ? (
      <ButtonIcon asChild {...commonButtonIconProps} icon={isSolo}>
        <Link
          title={title}
          to={pages[0].flatData.url}
          onClick={() => sendGaEvent(gaEvent as GaEvent)}
        />
      </ButtonIcon>
    ) : (
      <Button asChild {...commonButtonProps}>
        <Link
          title={commonButtonProps.title}
          to={pages[0].flatData.url}
          onClick={() => sendGaEvent(gaEvent as GaEvent)}
        >
          {buttonChildren}
        </Link>
      </Button>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.EXTERNAL_LINK) &&
    !isEmpty(href)
  ) {
    const resolvedHref = replaceLinkValues(href, currentSearchUrlParameters)

    ctaButton = isSoloLoading ? (
      <Skeleton
        variant="rectangle"
        width={standardIconSizeMap[size]}
        height={standardIconSizeMap[size]}
      />
    ) : isSolo ? (
      <ButtonIcon asChild {...commonButtonIconProps} icon={isSolo}>
        {/* biome-ignore lint/a11y/useAnchorContent: asChild — ButtonIcon provides icon and iconTitle as accessible label */}
        <a
          href={resolvedHref}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          onClick={() => sendGaEvent(gaEvent as GaEvent)}
        />
      </ButtonIcon>
    ) : (
      <LinkUds
        href={resolvedHref}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        onClick={() => sendGaEvent(gaEvent as GaEvent)}
        concept="mono"
      >
        {renderButton()}
      </LinkUds>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.ADD_TO_BASKET) &&
    !isEmpty(plan)
  ) {
    ctaButton = (
      <PriceWithButtonWrapper hasSurface={false}>
        <UdsBasketButton
          {...commonButtonProps}
          hidePrice={isEmpty(showPrice?.plan)}
          plan={plan}
          text={text}
          onClick={() => sendGaEvent(gaEvent as GaEvent)}
          buttonType={buttonType}
          icon={loadedIcon}
        />
      </PriceWithButtonWrapper>
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.FAST_CHECKOUT) &&
    !isEmpty(fastCheckoutPlan)
  ) {
    ctaButton = (
      <UdsFastCheckoutButton
        {...commonButtonProps}
        plan={fastCheckoutPlan}
        text={text}
        onClick={() => sendGaEvent(gaEvent as GaEvent)}
        buttonType={buttonType}
        icon={loadedIcon}
      />
    )
  }

  if (
    typeMatches(actionTypeName, SquidexComponentType.CTA_AX_LINK) &&
    !isEmpty(href) &&
    !isEmpty(plan)
  ) {
    const isPlanOverLimit = plansOverLimit.includes(plan[0].flatData.id)
    const resolvedHref = replaceLinkValues(href, currentSearchUrlParameters)

    const handleAxLinkClick = () => {
      sendGaEvent(gaEvent as GaEvent)

      if (isPlanOverLimit) {
        dispatch(showPlanOverLimitModal())
      }
    }

    ctaButton = isPlanOverLimit ? (
      renderButton(handleAxLinkClick)
    ) : isSoloLoading ? (
      <Skeleton
        variant="rectangle"
        width={standardIconSizeMap[size]}
        height={standardIconSizeMap[size]}
      />
    ) : isSolo ? (
      <ButtonIcon asChild {...commonButtonIconProps} icon={isSolo}>
        {/* biome-ignore lint/a11y/useAnchorContent: asChild — ButtonIcon provides icon and iconTitle as accessible label */}
        <a href={resolvedHref} onClick={handleAxLinkClick} />
      </ButtonIcon>
    ) : (
      <LinkUds href={resolvedHref} onClick={handleAxLinkClick} concept="mono">
        {renderButton()}
      </LinkUds>
    )
  }

  return (
    <UdsThemeProvider>
      <ConditionalWrapper
        wrapper={(children) => (
          <FlexContainerOrg alignItems="center" display="inline-flex">
            <ProductPrice isSmall plan={showPrice?.plan} className="mr-4" />

            {children}
          </FlexContainerOrg>
        )}
        condition={
          !isEmpty(showPrice?.plan) &&
          !typeMatches(actionTypeName, SquidexComponentType.ADD_TO_BASKET)
        }
      >
        {ctaButton}
      </ConditionalWrapper>
    </UdsThemeProvider>
  )
}

export default UdsButton
